import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
  const [token, setToken] = useState(() => localStorage.getItem("pa_token"));

  useEffect(() => {
    if (!token) {
      setUser(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => setUser(r.data.user))
      .catch(() => {
        localStorage.removeItem("pa_token");
        setToken(null);
        setUser(false);
      });
  }, [token]);

  const persist = useCallback((tok, usr) => {
    localStorage.setItem("pa_token", tok);
    setToken(tok);
    setUser(usr);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    persist(data.token, data.user);
    return data.user;
  }, [persist]);

  const register = useCallback(async (email, password, name, registration_path = 'citizen') => {
    const { data } = await api.post("/auth/register", { email, password, name, registration_path });
    persist(data.token, data.user);
    return data.user;
  }, [persist]);

  const googleLogin = useCallback(async (payload) => {
    const { data } = await api.post('/auth/google', payload);
    persist(data.token, data.user);
    return data;
  }, [persist]);

  const logout = useCallback(() => {
    const currentToken = localStorage.getItem("pa_token");
    if (currentToken) {
      api.post("/auth/logout", {}, { headers: { Authorization: `Bearer ${currentToken}` } }).catch(() => {});
    }
    window.google?.accounts?.id?.disableAutoSelect();
    localStorage.removeItem("pa_token");
    setToken(null);
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data.user);
    return data.user;
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, register, googleLogin, logout, refreshUser, isChecking: user === null }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

